import { useTranslate } from '@refinedev/core';
import { App as AntdApp, Input, Tag, Typography, theme } from 'antd';
import { Space } from 'antd';
import { useEffect, useState } from 'react';
import { SectionDrawer } from '../../components/ToolSection';
import { goalLabelProblem, setGoalInput } from './model';
import { useMotivationCommand } from './useMotivationCommand';

/**
 * E49 · SetChangeGoal (with the client) — the Set a change drawer, opened from the section header (a new change belongs to the section,
 * not to an existing card). The value chips carry a visible outline so they read as chips, not as plain text (N-8).
 */
type Tx = (key: string, options?: Record<string, unknown>) => string;

const TID = 'cc-client-motivation';

type Props = {
  open: boolean;
  clientId: string;
  /** The client's value names, by rank. */
  choices: string[];
  onClose: () => void;
  /** The new change's id (null when the answer carried none). */
  onSaved: (id: string | null) => void;
};

export const MiChangeDrawer = ({
  open,
  clientId,
  choices,
  onClose,
  onSaved,
}: Props) => {
  const t = useTranslate() as unknown as Tx;
  const { message } = AntdApp.useApp();
  const { token } = theme.useToken();
  const { run, isLoading } = useMotivationCommand();
  const [label, setLabel] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLabel('');
      setPicked([]);
      setError(null);
    }
  }, [open]);

  const onSave = async () => {
    if (goalLabelProblem(label)) {
      setError(t('mi.errors.MI_GOAL_LABEL_REQUIRED'));
      return;
    }
    setError(null);
    const res = await run(
      'setChangeGoal',
      setGoalInput(clientId, label, picked),
      t('mi.errors.MI_VALUE_INVALID'),
    );
    if (res.ok) {
      message.success(t('mi.hub.goal.saved'));
      onSaved(res.id);
    } else {
      setError(res.message);
    }
  };

  return (
    <SectionDrawer
      open={open}
      title={t('mi.hub.goal.title')}
      testId={`${TID}-goal`}
      context={
        <Typography.Text type="secondary">{t('mi.hub.goal.intro')}</Typography.Text>
      }
      saveLabel={t('mi.hub.goal.save')}
      saving={isLoading}
      error={error}
      dirty={label !== '' || picked.length > 0}
      onSave={() => {
        onSave().catch(() => undefined);
      }}
      onClose={onClose}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Typography.Text>{t('mi.hub.goal.label')}</Typography.Text>
          <Input
            data-testid={`${TID}-goal-label`}
            value={label}
            maxLength={200}
            onChange={(e) => {
              setLabel(e.target.value);
              setError(null);
            }}
          />
        </div>
        {choices.length > 0 ? (
          <div data-testid={`${TID}-goal-values`}>
            <Typography.Text>{t('mi.hub.goal.values')}</Typography.Text>
            <br />
            <Typography.Text type="secondary">
              {t('mi.hub.goal.valuesHint')}
            </Typography.Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap size={[8, 8]}>
                {choices.map((name, i) => {
                  const on = picked.includes(name);
                  return (
                    <Tag.CheckableTag
                      key={name}
                      data-testid={`${TID}-goal-value-${i}`}
                      checked={on}
                      style={{
                        border: `1px solid ${
                          on ? token.colorPrimary : token.colorBorder
                        }`,
                        margin: 0,
                        padding: '2px 10px',
                      }}
                      onChange={(next) => {
                        setPicked(
                          next
                            ? [...picked, name]
                            : picked.filter((x) => x !== name),
                        );
                        setError(null);
                      }}
                    >
                      {name}
                    </Tag.CheckableTag>
                  );
                })}
              </Space>
            </div>
          </div>
        ) : null}
      </Space>
    </SectionDrawer>
  );
};
